import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogContent } from '@angular/material/dialog';
import { Address, Customer } from '@app/core/stingray-api/models';
import { BuildingInfoService, CustomersService } from '@app/core/stingray-api/services';
import { CustomerService } from '@app/customer/customer.service';
import { finalize } from 'rxjs/operators';
import { createAddressFormGroup } from '../address-input-v2/address-input-v2-tools';
import { LoadingButtonComponent } from '../loading-button/loading-button.component';
import { AddressInputV2Component } from '../address-input-v2/address-input-v2.component';
import { MatButton } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SlicePipe } from '@angular/common';

export class BuildingOwnerRow {
  cadastralId: string;
  name: string;
}

@Component({
    selector: 'unite-find-cadastralid',
    templateUrl: './find-cadastralid.component.html',
    styleUrls: ['./find-cadastralid.component.scss'],
    imports: [MatDialogContent, MatProgressSpinner, TranslateModule, MatButton, ReactiveFormsModule, AddressInputV2Component, LoadingButtonComponent, SlicePipe]
})
export class FindCadastralIdComponent implements OnInit {

  customer: Customer;

  isValidAddress = false;
  addressFormGroup: UntypedFormGroup;

  isRetrievingOwnedAddresses = false;
  ownedAddresses: Address[] = [];

  retrievingOwnersForAddress: string;
  isRetrievingOwners = false;
  retrievedOwners = false;
  owners: BuildingOwnerRow[] = [];
  noOwnersFound = false;

  enterAddressManually = false;

  constructor(
    private matDialogRef: MatDialogRef<FindCadastralIdComponent>,
    private formBuilder: UntypedFormBuilder,
    private buildingInfoService: BuildingInfoService,
    private customerService: CustomerService,
    private customersService: CustomersService
  ) { }

  ngOnInit(): void {

    this.customerService.getCustomer().subscribe(c => {
      this.customer = c;
    });

    this.addressFormGroup = this.formBuilder.group({
      address: createAddressFormGroup(this.formBuilder),
    });

    this.addressFormGroup.get('address').valueChanges.subscribe(() => {
      this.isValidAddress = this.addressFormGroup.get('address').valid;
      this.retrievedOwners = false;
      this.isRetrievingOwners = false;
    });

    this.isRetrievingOwnedAddresses = true;
    this.customersService.getCustomerBuildingsById({
      id: this.customer.id,
      statuses: ['Owned']
    })
      .pipe(finalize(() => this.isRetrievingOwnedAddresses = false))
      .subscribe(b => {
        this.ownedAddresses = b.map(a => a.address);
        this.enterAddressManually = b.length === 0;
      });

  }

  retrieveOwnersForOwnedAddress(address: Address): void {

    this.retrievingOwnersForAddress = this.getAddressLine(address);

    this.findOwnersForAddress(address.postalCode, address.houseNumber, address.houseNumberAddition);
  }

  retrieveOwnersForManualAddress(): void {
    const postalCode = this.addressFormGroup.get('address').value.postalCode;
    const houseNumber = this.addressFormGroup.get('address').value.houseNumber;
    const houseNumberAddition = this.addressFormGroup.get('address').value.houseNumberAddition;

    this.retrievingOwnersForAddress = `${this.addressFormGroup.get('address').value.street} ${houseNumber}`;

    if (houseNumberAddition) {
      this.retrievingOwnersForAddress = `${this.retrievingOwnersForAddress} ${houseNumberAddition}`;
    }

    this.findOwnersForAddress(postalCode, houseNumber, houseNumberAddition);
  }

  getAddressLine(address: Address): string {
    if (!address) {
      return null;
    }
    return `${address.addressLine1}, ${address.city}`;
  }

  connect(cadastralId: string): void {
    this.matDialogRef.close(cadastralId);
  }

  private findOwnersForAddress(postalCode: any, houseNumber: any, houseNumberAddition: any): void {
    this.isRetrievingOwners = true;
    this.noOwnersFound = false;

    this.buildingInfoService
      .getBuildingOwnerInfo({ postalCode, houseNumber, houseNumberAddition })
      .pipe(finalize(() => {
        this.isRetrievingOwners = false;
        this.retrievedOwners = true;
      }))
      .subscribe(owners => {
        if (owners) {
          this.noOwnersFound = false;
          this.owners = [];

          owners.persons.forEach(person => {
            this.owners.push({
              cadastralId: person.cadastralId,
              name: person.fullName
            } as BuildingOwnerRow);
          });

          owners.companies.forEach(company => {
            this.owners.push({
              cadastralId: company.cadastralId,
              name: company.name
            } as BuildingOwnerRow);
          });

        } else {
          this.noOwnersFound = true;
        }
      });
  }
}
